import { notifyError } from "@/lib/notify-error";
import { useMemo, useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id, Doc } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Eye, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useRouteParam } from "@/lib/use-route-param";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { useProject } from "@/components/projects";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";

import { PageHeader, PageHeaderSkeleton, type Crumb } from "@/components/layout";
import { JobStatusBadge } from "@/components/job-status-badge";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { withExportExtension } from "@/lib/export-filenames";
import { downloadUrlAsFile } from "@/lib/document-download";
import { ExportButton } from "@/features/exports/export-button";
import { exportTarget } from "@/features/exports/export-names";
import { useExportFilenames } from "@/features/exports/use-export-filenames";
import { SubmittalExportDialog } from "@/features/spec-pilot/submittal/submittal-export-dialog";
import { ExtractedTableList } from "@/features/spec-pilot/job-detail/extracted-table-list";
import {
  excludedMemberTableIds,
  hasUngeneratedChanges,
  type GeneratedMerge,
} from "@/features/spec-pilot/job-detail/merge-layout";
import {
  SubmittalBar,
  type GenerateOptions,
} from "@/features/spec-pilot/job-detail/submittal-bar";
import { TableRowsPanel } from "@/features/spec-pilot/job-detail/table-rows-panel";
import { useRowGroupActions } from "@/features/spec-pilot/job-detail/use-row-group-actions";
import { useSubmittalGeneration } from "@/features/spec-pilot/job-detail/use-submittal-generation";
import type { RowGroup, TableMetadata } from "@/features/spec-pilot/job-detail/types";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { useRecordExportDownloaded } from "@/features/onboarding";
import { RequiresCapability } from "@/lib/capabilities";

export const Route = createFileRoute("/specPilot/$jobId")({
  component: JobDetailPage,
});

// Type definitions
type Job = Doc<"specPilotSubmittals"> & { originalFilename?: string };
type Submittal = {
  _id: Id<"specPilotGeneratedSubmittals">;
  _creationTime: number;
  submittalId: Id<"specPilotSubmittals">;
  generatedFileKey?: string;
  generatedAt: number;
  includedTableIds: Id<"specPilotExtractedTables">[];
  operationId?: Id<"operations">;
  templateId?: string;
  generatedRowGroups: GeneratedMerge[] | null;
  editedAt?: number;
  hasEditedData: boolean;
};

const SPEC_PILOT_TRAIL: Crumb[] = [{ label: "SpecPilot", href: "/specPilot" }];

function JobPageHeader({ job, actions }: { job: Job; actions?: ReactNode }) {
  return (
    <PageHeader
      title={job.name}
      trail={SPEC_PILOT_TRAIL}
      meta={<JobStatusBadge status={job.status} />}
      actions={actions}
    />
  );
}

export function JobDetailPage() {
  const jobId = useRouteParam("jobId");
  useProject(); // Ensure project context is available for child components
  const { data: jobDetails } = useQuery(convexQuery(api.specPilot.submittals.getWithDetails, {
    submittalId: jobId as Id<"specPilotSubmittals">,
  }));

  // Loading state
  if (jobDetails === undefined) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <Card>
          <ProcessingSpinner message="Loading job details..." />
        </Card>
      </div>
    );
  }

  // Not found state
  if (jobDetails === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Job Not Found" trail={SPEC_PILOT_TRAIL} />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Job not found</AlertTitle>
          <AlertDescription>
            The job you are looking for does not exist or has been deleted.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { job, project: jobProject, tables, submittal, rowGroups } = jobDetails;
  const safeRowGroups = rowGroups ?? [];
  const projectName = jobProject?.name;

  let content: ReactNode = null;
  switch (job.status) {
    case "pending":
    case "processing":
      content = (
        <ProcessingState job={job} />
      );
      break;
    case "failed":
      content = (
        <FailedState
          job={job}
          tables={tables}
          rowGroups={safeRowGroups}
        />
      );
      break;
    case "ready_for_review":
    case "completed":
      content = (
        <JobWorkspace
          job={job}
          mode={job.status === "completed" && submittal ? "regenerate" : "generate"}
          tables={tables}
          rowGroups={safeRowGroups}
          submittal={submittal}
          headerActions={
            job.status === "completed" && submittal ? (
              <CompletedHeaderActions job={job} projectName={projectName} submittal={submittal} />
            ) : null
          }
        />
      );
      break;
    default:
      content = null;
  }

  return (
    <ActionLogProvider
      projectId={job.projectId}
      pilot="SpecPilot"
      targetKind="specPilotSubmittal"
      targetId={job._id}
    >
      {content}
    </ActionLogProvider>
  );
}

// -----------------------------------------------------------------------------
// Processing State
// -----------------------------------------------------------------------------

interface ProcessingStateProps {
  job: Job;
}

function ProcessingState({ job }: ProcessingStateProps) {
  return (
    <div className="space-y-4">
      <JobPageHeader job={job} />

      <Card>
        <ProcessingSpinner
          message={
            job.originalFilename
              ? `Extracting tables from ${job.originalFilename}...`
              : "Extracting tables from your PDF..."
          }
        />
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Failed State
// -----------------------------------------------------------------------------

interface FailedStateProps {
  job: Job;
  tables: TableMetadata[];
  rowGroups: RowGroup[];
}

function FailedState({ job, tables, rowGroups }: FailedStateProps) {
  const navigate = useNavigate();
  const removeJob = useMutation(api.specPilot.submittals.remove);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await removeJob({ submittalId: job._id });
      toast.success("Job deleted successfully");
      navigate({ to: "/specPilot" });
    } catch (error) {
      notifyError(error, "Failed to delete job");
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  }

  const deleteAction = (
    <RequiresCapability mode="hide">
      <DeleteConfirmationDialog
        title="Delete this job?"
        description="This action cannot be undone. This will permanently delete the job and all associated data."
        onConfirm={handleDelete}
        disabled={isDeleting}
      >
        <Button variant="destructive" size="sm" disabled={isDeleting}>
          <Trash2 />
          Delete Job
        </Button>
      </DeleteConfirmationDialog>
    </RequiresCapability>
  );

  const alert = (
    <Alert variant="destructive">
      <AlertCircle className="size-4" />
      <AlertTitle>Processing Failed</AlertTitle>
      <AlertDescription>
        {job.error || "An unknown error occurred while processing this job."}
      </AlertDescription>
    </Alert>
  );

  if (tables.length === 0) {
    return (
      <div className="space-y-4">
        <JobPageHeader job={job} actions={deleteAction} />
        {alert}
      </div>
    );
  }

  return (
    <JobWorkspace
      job={job}
      mode="retry"
      tables={tables}
      rowGroups={rowGroups}
      submittal={null}
      headerActions={deleteAction}
      notice={alert}
    />
  );
}

// -----------------------------------------------------------------------------
// Job Workspace (Review, Completed, and retryable Failed jobs)
// -----------------------------------------------------------------------------

const GENERATION_MESSAGES = {
  generate: {
    successMessage: "Submittal generated successfully",
    errorMessage: "Failed to generate submittal",
  },
  regenerate: {
    successMessage: "Submittal regeneration started",
    errorMessage: "Failed to regenerate submittal",
  },
  retry: {
    successMessage: "Submittal generation retry started",
    errorMessage: "Failed to retry submittal generation",
  },
} as const;

interface JobWorkspaceProps {
  job: Job;
  mode: "generate" | "regenerate" | "retry";
  tables: TableMetadata[];
  rowGroups: RowGroup[];
  submittal: Submittal | null;
  headerActions?: ReactNode;
  notice?: ReactNode;
}

/**
 * Table inspector: the extracted tables on the left, the active table's rows on the right
 * (where rows are merged), and the submittal bar docked at the bottom.
 */
function JobWorkspace({
  job,
  mode,
  tables,
  rowGroups,
  submittal,
  headerActions,
  notice,
}: JobWorkspaceProps) {
  const recordActionLog = useRecordActionLog();
  const { handleSelectionChange, handleCreateGroup, handleUpdateGroup, handleDeleteGroup } =
    useRowGroupActions(job._id, recordActionLog);
  const { isGenerating, runGeneration } = useSubmittalGeneration(job._id);
  const appNavigate = useAppNavigate();
  const { data: sourcePdf } = useQuery(
    convexQuery(api.specPilot.submittals.getSourcePdfUrl, {
      submittalId: job._id,
    })
  );

  // Open the first included table; pinned so excluding it doesn't swap the view to another.
  const [chosenTableId, setChosenTableId] = useState(
    () => (tables.find((table) => table.selectedForSubmittal) ?? tables[0])?._id
  );
  const activeTable = tables.find((table) => table._id === chosenTableId) ?? tables[0];

  const selectedTableIds = useMemo(
    () => new Set<string>(tables.filter((table) => table.selectedForSubmittal).map((table) => table._id)),
    [tables]
  );
  const tableNameById = useMemo(
    () => new Map<string, string>(tables.map((table, index) => [table._id, table.name || `Table ${index + 1}`])),
    [tables]
  );
  const mergeCountByTable = useMemo(() => {
    const counts = new Map<string, number>();
    for (const group of rowGroups) {
      for (const tableId of new Set(group.members.map((member) => member.tableId))) {
        counts.set(tableId, (counts.get(tableId) ?? 0) + 1);
      }
    }
    return counts;
  }, [rowGroups]);
  const activeMergeCount = rowGroups.filter(
    (group) => excludedMemberTableIds(group, selectedTableIds).length === 0
  ).length;

  async function handleGenerate(options: GenerateOptions) {
    const success = await runGeneration({
      selectedCount: selectedTableIds.size,
      templateId: options.templateId,
      templateDefaultOverrides: options.templateDefaultOverrides,
      ...GENERATION_MESSAGES[mode],
      emptySelectionMessage: "Include at least one table before generating",
    });
    if (success) {
      await recordActionLog();
      appNavigate({
        to: "/specPilot/$jobId/submittal",
        params: { jobId: job._id },
        title: `${job.name} - Submittal`,
      });
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <JobPageHeader job={job} actions={headerActions} />
      {notice}

      {tables.length === 0 || !activeTable ? (
        <Card className="py-8">
          <CardContent className="text-center">
            <p className="text-muted-foreground">No tables were extracted from this PDF.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
            <ExtractedTableList
              tables={tables}
              activeTableId={activeTable._id}
              sourceDocument={job.originalFilename}
              mergeCountByTable={mergeCountByTable}
              onActivate={setChosenTableId}
              onSelectionChange={(tableId, selected) => void handleSelectionChange(tableId, selected)}
            />
            <TableRowsPanel
              table={activeTable}
              tableName={tableNameById.get(activeTable._id) ?? "Untitled Table"}
              rowGroups={rowGroups}
              selectedTableIds={selectedTableIds}
              tableNameById={tableNameById}
              onIncludeTable={(tableId) => void handleSelectionChange(tableId, true)}
              onCreateGroup={handleCreateGroup}
              onUpdateGroup={handleUpdateGroup}
              onDeleteGroup={handleDeleteGroup}
              sourcePdf={sourcePdf}
              sourceId={job._id}
            />
          </div>

          <SubmittalBar
            mode={mode}
            selectedTableCount={selectedTableIds.size}
            totalTableCount={tables.length}
            activeMergeCount={activeMergeCount}
            isGenerating={isGenerating}
            onGenerate={handleGenerate}
            generated={
              submittal
                ? {
                    generatedAt: submittal.generatedAt,
                    hasEditedData: submittal.hasEditedData,
                    templateId: submittal.templateId,
                    hasUngeneratedChanges: hasUngeneratedChanges({
                      tables,
                      groups: rowGroups,
                      includedTableIds: submittal.includedTableIds,
                      generatedAt: submittal.generatedAt,
                      generatedMerges: submittal.generatedRowGroups,
                    }),
                  }
                : undefined
            }
          />
        </>
      )}
    </div>
  );
}

function CompletedHeaderActions({
  job,
  projectName,
  submittal,
}: {
  job: Job;
  projectName: string | undefined;
  submittal: Submittal;
}) {
  const appNavigate = useAppNavigate();
  return (
    <>
      <Button
        size="sm"
        onClick={() =>
          appNavigate({
            to: "/specPilot/$jobId/submittal",
            params: { jobId: job._id },
            title: `${job.name} - Submittal`,
          })
        }
      >
        <Eye />
        View Submittal
      </Button>
      <SubmittalCsvExport
        submittalId={job._id}
        fileKey={submittal.generatedFileKey}
        projectName={projectName}
      />
    </>
  );
}

// -----------------------------------------------------------------------------
// CSV Export
// -----------------------------------------------------------------------------

function SubmittalCsvExport({
  submittalId,
  fileKey,
  projectName,
}: {
  submittalId: Id<"specPilotSubmittals">;
  fileKey?: string;
  projectName: string | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const recordExportDownloaded = useRecordExportDownloaded();
  const exportFilenames = useExportFilenames({ kind: "specPilotSubmittal", id: submittalId });
  const target = exportTarget("spec_pilot_submittal_csv", {}, { projectName });
  const { data: storageUrl } = useQuery(
    convexQuery(
      api.specPilot.generatedSubmittals.getStorageUrl,
      fileKey ? { fileKey } : "skip"
    )
  );

  async function downloadCsv(fileName: string) {
    if (!storageUrl) return false;
    try {
      await downloadUrlAsFile(storageUrl, withExportExtension(fileName, "csv"));
      recordExportDownloaded(submittalId);
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download file");
      console.error(error);
      return false;
    }
  }

  return (
    <>
      <ExportButton onClick={() => setIsOpen(true)} disabled={!storageUrl} />
      <SubmittalExportDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        version="generated"
        defaultFileName={exportFilenames.resolve(target.key, target.defaultName)}
        downloadCsv={downloadCsv}
        canDownloadCsv={Boolean(storageUrl)}
        exportFilenames={exportFilenames}
      />
    </>
  );
}
